import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, Button, Form, Input, Modal, Select, Space, Steps, message, notification } from 'antd';

import {
  listCategoriesAdmin,
  listDificuldades,
  type CatalogKind,
  type CategoryAdmin,
  type Dificuldade,
  type EdicaoAdmin,
} from '../../../services/admin';
import { getModule } from '../Editor/registry';
import { ImportXmlCard } from '../Editor/ImportXmlCard';
import {
  NewPuzzleFields,
  buildNewPuzzleParams,
  newPuzzleInitialValues,
  type NewPuzzleFormValues,
} from '../Editor/NewPuzzleModal';
import { CATALOG_KINDS, creationMethods, editorKindOf, kindLabel, type CreationMethod } from './catalog-kind';
import { EdicaoCategoriesLine } from './EdicaoCategoriesLine';
import { EdicaoSelect } from './EdicaoSelect';
import { TemaSelect } from './TemaSelect';
import { applyCreationMeta, type CreationMeta, type CreationSource } from './apply-creation-meta';
import { CrosswordGenerator } from './generators/CrosswordGenerator';
import { QuizForm } from './generators/QuizForm';
import { SudokuGenerator } from './generators/SudokuGenerator';
import { WordsearchGenerator } from './generators/WordsearchGenerator';
import type { GeneratorProps } from './generators/types';

// "+ Novo jogo": o único caminho de criação do admin, em 3 passos — Tipo → Como criar →
// Informações. Os caminhos de cada tipo vêm de `creationMethods` (um só = o passo 2 é
// pulado). Nada é gravado enquanto o modal está aberto, exceto pelo botão do gerador/quiz:
// "Montar no editor" e "Importar XML" só levam ao editor, e quem grava é o "Salvar" dele
// (que aplica edição/tema/dificuldade depois de criar o registro).

type WizardKind = Exclude<CatalogKind, 'other'>;

interface Props {
  open: boolean;
  onClose: () => void;
  /** Chamado quando um registro foi criado aqui mesmo (gerador/quiz) — a lista recarrega. */
  onCreated: () => void;
}

interface FormValues extends NewPuzzleFormValues {
  dificuldadeId?: number;
}

const METHOD_LABELS: Record<CreationMethod, string> = {
  import: 'Importar XML',
  generate: 'Gerar automaticamente',
  editor: 'Montar no editor',
};

const GENERATORS: Partial<Record<WizardKind, React.FC<GeneratorProps>>> = {
  'crossword-traditional': CrosswordGenerator,
  wordsearch: WordsearchGenerator,
  sudoku: SudokuGenerator,
};

/** Cruzadas moram em `passatempos`; o resto em `jogos`. Decide a API do meta. */
const sourceOf = (kind: WizardKind): CreationSource =>
  kind === 'crossword-traditional' || kind === 'crossword-direct' ? 'passatempo' : 'jogo';

const STEP_KIND = 0;
const STEP_METHOD = 1;
const STEP_INFO = 2;

export const NewGameWizard: React.FC<Props> = ({ open, onClose, onCreated }) => {
  const navigate = useNavigate();
  const [form] = Form.useForm<FormValues>();
  const [step, setStep] = useState(STEP_KIND);
  const [kind, setKind] = useState<WizardKind | null>(null);
  const [method, setMethod] = useState<CreationMethod | null>(null);
  const [edicao, setEdicao] = useState<{ id: number; nome: string | null; categoryIds: number[] } | null>(
    null,
  );
  const [tema, setTema] = useState<{ id: number; nome: string | null } | null>(null);
  const [dificuldades, setDificuldades] = useState<Dificuldade[]>([]);
  const [categories, setCategories] = useState<CategoryAdmin[]>([]);
  const titulo: string | undefined = Form.useWatch('titulo', form);
  // Observado (não lido com getFieldValue): o meta do import é montado no render, e
  // escolher a dificuldade por último precisa re-renderizar o card com o valor novo.
  const dificuldadeId: number | undefined = Form.useWatch('dificuldadeId', form);

  // Cada abertura começa do zero.
  useEffect(() => {
    if (!open) return;
    setStep(STEP_KIND);
    setKind(null);
    setMethod(null);
    setEdicao(null);
    setTema(null);
    listDificuldades()
      .then(setDificuldades)
      .catch(() => setDificuldades([]));
    listCategoriesAdmin()
      .then(setCategories)
      .catch(() => setCategories([]));
  }, [open]);

  const editorKind = kind ? editorKindOf(kind) : undefined;
  const editorModule = editorKind ? getModule(editorKind) : undefined;
  const newPuzzle = editorModule?.newPuzzle;

  const chooseKind = (k: WizardKind) => {
    setKind(k);
    const methods = creationMethods(k);
    if (methods.length === 1) {
      setMethod(methods[0]);
      setStep(STEP_INFO);
    } else {
      setMethod(null);
      setStep(STEP_METHOD);
    }
  };

  const chooseMethod = (m: CreationMethod) => {
    setMethod(m);
    setStep(STEP_INFO);
  };

  const back = () => {
    if (step === STEP_INFO && kind && creationMethods(kind).length > 1) setStep(STEP_METHOD);
    else setStep(STEP_KIND);
  };

  const buildMeta = (): CreationMeta => {
    const meta: CreationMeta = {};
    if (edicao) meta.edicoesId = edicao.id;
    if (tema) meta.temaId = tema.id;
    if (dificuldadeId !== undefined && dificuldadeId !== null) meta.dificuldadeId = dificuldadeId;
    return meta;
  };

  const resolveTitle = (): Promise<string | null> =>
    form
      .validateFields(['titulo'])
      .then((values) => values.titulo.trim())
      .catch(() => null);

  const openInEditor = async () => {
    if (!editorKind || !newPuzzle) return;
    let values: FormValues;
    try {
      values = await form.validateFields();
    } catch {
      return;
    }
    const params = buildNewPuzzleParams(newPuzzle, values);
    const meta = buildMeta();
    onClose();
    navigate(`/admin/editor/${editorKind}`, { state: { params, meta } });
  };

  const handleCreated = async (id: number) => {
    if (!kind) return;
    try {
      await applyCreationMeta(sourceOf(kind), id, buildMeta());
    } catch {
      message.warning(
        'Jogo criado, mas edição, tema e dificuldade não foram gravados. Ajuste em Informações.',
        8,
      );
    }
    const key = `new-game-${id}`;
    const target = editorModule ? `/admin/editor/${editorModule.kind}/${id}` : null;
    notification.success({
      key,
      message: `Jogo criado (#${id}).`,
      btn: target ? (
        <Button
          type="primary"
          size="small"
          onClick={() => {
            notification.close(key);
            navigate(target);
          }}
        >
          Abrir no editor
        </Button>
      ) : undefined,
    });
    onCreated();
    onClose();
  };

  const onEdicaoChange = (id?: number, picked?: EdicaoAdmin) => {
    setEdicao(
      id === undefined
        ? null
        : { id, nome: picked?.nome ?? null, categoryIds: picked?.categoryIds ?? [] },
    );
    // Cascata: o tema pertence à edição.
    setTema(null);
  };

  const Generator = kind === 'quiz' ? QuizForm : method === 'generate' && kind ? GENERATORS[kind] : undefined;
  const isEditorAction = method === 'editor' && kind !== 'quiz' && Boolean(newPuzzle);
  const isImport = method === 'import';

  const renderKindStep = () => (
    <Space wrap>
      {CATALOG_KINDS.map((k) => (
        <Button key={k} size="large" type={k === kind ? 'primary' : 'default'} onClick={() => chooseKind(k)}>
          {kindLabel(k)}
        </Button>
      ))}
    </Space>
  );

  const renderMethodStep = () =>
    kind && (
      <Space wrap>
        {creationMethods(kind).map((m) => (
          <Button key={m} size="large" type={m === method ? 'primary' : 'default'} onClick={() => chooseMethod(m)}>
            {METHOD_LABELS[m]}
          </Button>
        ))}
      </Space>
    );

  const renderInfoStep = () => {
    return (
      <>
        {isEditorAction && newPuzzle?.notice && (
          <Alert
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
            message={newPuzzle.notice.message}
            description={newPuzzle.notice.description}
          />
        )}
        <Form
          form={form}
          layout="vertical"
          preserve={false}
          initialValues={newPuzzle && isEditorAction ? newPuzzleInitialValues(newPuzzle) : undefined}
        >
          <Form.Item
            name="titulo"
            label="Título"
            rules={isImport ? [] : [{ required: true, whitespace: true, message: 'Informe o título.' }]}
            extra={isImport ? 'Vazio = usa o título do arquivo.' : undefined}
          >
            <Input placeholder={newPuzzle?.titlePlaceholder} />
          </Form.Item>
          <Form.Item label="Edição">
            <div data-testid="edicao-field">
              <EdicaoSelect
                value={edicao?.id}
                valueLabel={edicao?.nome}
                onChange={onEdicaoChange}
                style={{ width: '100%' }}
              />
            </div>
            {edicao && <EdicaoCategoriesLine categoryIds={edicao.categoryIds} categories={categories} />}
          </Form.Item>
          <Form.Item label="Tema">
            <TemaSelect
              edicoesId={edicao?.id}
              value={tema?.id}
              valueLabel={tema?.nome}
              onChange={(id, picked) => setTema(id === undefined ? null : { id, nome: picked?.nome ?? null })}
              style={{ width: '100%' }}
            />
          </Form.Item>
          <Form.Item name="dificuldadeId" label="Dificuldade">
            <Select
              allowClear
              placeholder="Dificuldade"
              options={dificuldades.map((d) => ({ value: d.id, label: d.nome }))}
            />
          </Form.Item>
          {isEditorAction && newPuzzle && (
            <NewPuzzleFields config={newPuzzle} active={open} tipoLabel="Formato" tipoExtra={null} />
          )}
          {isImport && editorKind && (
            <ImportXmlCard kind={editorKind} titulo={titulo} meta={buildMeta()} onNavigate={onClose} />
          )}
          {Generator && <Generator resolveTitle={resolveTitle} onCreated={(id) => void handleCreated(id)} />}
        </Form>
      </>
    );
  };

  const footer = [
    <Button key="cancel" onClick={onClose}>
      Cancelar
    </Button>,
    step > STEP_KIND && (
      <Button key="back" onClick={back}>
        Voltar
      </Button>
    ),
    step === STEP_INFO && isEditorAction && (
      <Button key="editor" type="primary" onClick={openInEditor}>
        Criar e abrir no editor
      </Button>
    ),
  ];

  return (
    <Modal title="Novo jogo" visible={open} onCancel={onClose} footer={footer} width={720} destroyOnClose>
      <Steps current={step} size="small" style={{ marginBottom: 24 }}>
        <Steps.Step title="Tipo" />
        <Steps.Step title="Como criar" />
        <Steps.Step title="Informações" />
      </Steps>
      {step === STEP_KIND && renderKindStep()}
      {step === STEP_METHOD && renderMethodStep()}
      {step === STEP_INFO && renderInfoStep()}
    </Modal>
  );
};

export default NewGameWizard;
